/**
 * Sparkline - Mini SVG frequency chart
 *
 * Renders a small area chart with optional data points.
 * Used for activity-over-time in the History tab.
 */

import React, { useMemo } from 'react';
import { Box } from '@mui/material';

function Sparkline({
  data = [],
  width = 200,
  height = 40,
  color = '#23A38F',
  showArea = true,
  showPoints = false,
  padding = 4,
}) {
  const chartData = useMemo(() => {
    if (!data.length) return null;

    const values = data.map((d) => (typeof d === 'number' ? d : d.value || 0));
    const maxVal = Math.max(...values, 1);
    const minVal = Math.min(...values, 0);
    const range = maxVal - minVal || 1;

    const innerWidth = width - padding * 2;
    const innerHeight = height - padding * 2;
    const step = innerWidth / Math.max(values.length - 1, 1);

    const points = values.map((val, i) => ({
      x: padding + i * step,
      y: padding + innerHeight - ((val - minVal) / range) * innerHeight,
    }));

    // SVG path for the line
    const linePath = points.map((p, i) =>
      `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    ).join(' ');

    // SVG path for the filled area
    const areaPath = linePath +
      ` L ${points[points.length - 1].x} ${height - padding}` +
      ` L ${padding} ${height - padding} Z`;

    return { points, linePath, areaPath };
  }, [data, width, height, padding]);

  if (!chartData) {
    return (
      <Box sx={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width={width} height={height}>
          <line
            x1={padding}
            y1={height / 2}
            x2={width - padding}
            y2={height / 2}
            stroke="#ddd"
            strokeWidth={1}
            strokeDasharray="4 2"
          />
        </svg>
      </Box>
    );
  }

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {/* Area fill */}
      {showArea && (
        <path
          d={chartData.areaPath}
          fill={`${color}20`}
          stroke="none"
        />
      )}

      {/* Line */}
      <path
        d={chartData.linePath}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Data points */}
      {showPoints &&
        chartData.points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={2.5}
            fill={color}
            stroke="white"
            strokeWidth={1}
          />
        ))}
    </svg>
  );
}

export default Sparkline;
